import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { Accept, useDropzone } from "react-dropzone";
import { ReactComponent as CloudUploadIcon } from "../../images/cloud-upload-icon.svg";
import { ReactComponent as PDFIcon } from "../../images/pdf-icon.svg";
import { ReactComponent as TrashIcon } from "../../images/trash-icon.svg";
import "./inputStyles.scss";
import { toast } from "react-toastify";
import { sizeConversion } from "../../helpers/GenericHelpers";

interface IFileInputProps {
  value: any;
  accept?: Accept;
  maxFiles?: number;
  noClick?: boolean;
  noDrag?: boolean;
  maxSize?: number;
  label?: string | ReactNode;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  showPreview?: boolean;
  onChange?: Function;
  secondaryLabel?: string;
}

const FileInput = (props: IFileInputProps) => {
  // props
  const {
    value,
    accept,
    maxFiles = 1,
    noClick = false,
    noDrag = false,
    maxSize = 1 * 1024 * 1024,
    label,
    disabled,
    required,
    className,
    showPreview = true,
    onChange,
    secondaryLabel,
  } = props;

  // states
  const [files, setFiles] = useState<any>([]);
  const [errors, setErrors] = useState<any>();

  // func - handlers
  const onDrop = (acceptedFiles: any, fileRejections: any, event: any) => {
    fileRejections?.length > 0 && setErrors(fileRejections);
    setFiles(acceptedFiles);
    onChange && onChange(acceptedFiles);
  };

  const removeFile = (file: any) => {
    const remainingFiles = files.filter((each: any) => each.name !== file.name);
    setFiles(remainingFiles);
    onChange && onChange(remainingFiles);
    acceptedFiles.splice(file, 1);
  };

  // dropzone hook
  const { acceptedFiles, fileRejections, getRootProps, getInputProps } =
    useDropzone({
      accept,
      maxFiles,
      noClick,
      noDrag,
      disabled,
      maxSize,
      onDrop,
    });

  // func - display
  const thumbs = useMemo(
    () =>
      files.map((file: any) => (
        <div className="preview-item" key={file.name}>
          <div className="remove" onClick={() => removeFile(file)}>
            <TrashIcon />
          </div>
          <div className="preview-icon">
            <PDFIcon />
          </div>
          <div className="preview-details">
            <div className="preview-primary">{file?.name ?? ""}</div>
            <div className="preview-secondary">{file?.size ? sizeConversion(file?.size) :  ""}</div>
          </div>
        </div>
      )),
    [JSON.stringify(files)]
  );

  // useEffects
  // useEffect(() => {
  //   // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
  //   return () =>
  //     files.forEach((file: any) => URL.revokeObjectURL(file.preview));
  // }, []);

  useEffect(() => setFiles(value ?? []), [JSON.stringify(value)]);

  useEffect(() => {
    if (errors && errors?.length > 0) {
      errors.forEach((err: any) => {
        err?.errors?.forEach((each: any) => {
          if (each?.code === "file-too-large" && maxSize) {
            toast.error(
              `Failed to upload. Size limit is ${sizeConversion(maxSize)}.`
            );
          } else if (each?.code === "file-invalid-type" && accept) {
            toast.error(
              `Valid formats for file upload are ${JSON.stringify(accept)}.`
            );
          } else {
            toast.error(each?.message);
          }
        });
      });
      setErrors([]);
    }
  }, [accept, errors, maxSize]);

  return (
    <div className={`${className ?? ""} file-input-container`}>
      <div {...getRootProps({ className: "dropzone" })}>
        <input {...getInputProps()} />
        {label ?? (
          <>
            <div className="file-upload-icon">
              <CloudUploadIcon />
            </div>
            <div className="file-upload-primary">
              Choose a file or drag & drop it here
            </div>
            <div className="file-upload-secondary">
              {secondaryLabel ?? 'JPEG, PNG, PDG, and MP4 formats, up to 50MB'}
            </div>
            <div className="file-upload-btn">Browse File</div>
          </>
        )}
      </div>
      {showPreview ? <div className="preview-container">{thumbs}</div> : <></>}
    </div>
  );
};

export default FileInput;
